<template>
  <CContainer>
    <CCard v-if="isOrganizationPlanSuspended">
      <CCardHeader class="bg-gray-200 pt-4">
        <section class="d-flex justify-content-center align-items-center">
          <div class="card-header-actions">
            {{ $t('managePlan.text.suspended-plan') }}
          </div>
        </section>
      </CCardHeader>
    </CCard>
    <CRow class="">
      <CCol sm="12" v-if="loading" class="vh-100">
        <div class="d-flex justify-content-center align-items-center" style="height: 30vh">
          <CSpinner color="custom-color" style="width: 4rem; height: 4rem" />
        </div>
      </CCol>
      <template v-else>
        <CCol sm="12" class="stepOneFirstBlock">
          <CRow>
            <CCol sm="12 mt-4">
              <div class="text-center">
                <Steps :currentStep="currentStep" />
                <div class="col-md-12" style="border-bottom: 1px solid #d3d3d3; margin-top: 20px"></div>
                <CCard class="w-50 mx-auto mt-4 shadow-sm" v-if="isOrganizationApproved === false">
                  <CCardHeader class="bg-purple-100">
                    <h1 class="font-2xl">{{ $t('managePlan.text.account-under-review.title') }}</h1>
                  </CCardHeader>
                  <CCardBody class="bg-gray-100">
                    <p class="mb-0">{{ $t('managePlan.text.account-under-review.description') }}</p>
                  </CCardBody>
                </CCard>
                <h3 v-if="currentStep == 1" class="mt-5 titleBusiness">
                  {{ $t('managePlan.text.chooseYourPlan') }}<CIcon class="linhaBusiness" name="linhaBusiness" :height="25" viewBox="0 0 186 32" />
                </h3>
                <div v-if="currentStep == 1" class="mb-1 mx-auto subTitleBusiness" style="max-width: 445px">
                  {{ $t('managePlan.text.presentation') }}
                </div>
              </div>
              <div class="row col-md-8 offset-md-2 mt-4" v-if="currentStep == 1">
                <div class="row bs-wizard col-md-12" style="border-bottom: 0">
                  <div :class="{ complete: maxAudience >= 2500 }" class="col bs-wizard-step" @click="changeMaxAudience(500)">
                    <div class="text-center bs-wizard-stepnum">500</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>
                  <!-- complete | active | disabled -->
                  <div
                    :class="{ complete: maxAudience >= 5000, disabled: maxAudience < 2500, active: maxAudience == 2500 }"
                    class="col-2 bs-wizard-step"
                    @click="changeMaxAudience(2500)"
                  >
                    <div class="text-center bs-wizard-stepnum">2.500</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>

                  <div
                    :class="{ complete: maxAudience >= 10000, disabled: maxAudience < 5000, active: maxAudience == 5000 }"
                    class="col bs-wizard-step"
                    @click="changeMaxAudience(5000)"
                  >
                    <div class="text-center bs-wizard-stepnum">5.000</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>

                  <div
                    :class="{ complete: maxAudience >= 25000, disabled: maxAudience < 10000, active: maxAudience == 10000 }"
                    class="col bs-wizard-step"
                    @click="changeMaxAudience(10000)"
                  >
                    <div class="text-center bs-wizard-stepnum">10.000</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>
                  <div
                    :class="{ complete: maxAudience >= 50000, disabled: maxAudience < 25000, active: maxAudience == 25000 }"
                    class="col bs-wizard-step"
                    @click="changeMaxAudience(25000)"
                  >
                    <div class="text-center bs-wizard-stepnum">25.000</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>
                  <div
                    :class="{ complete: maxAudience > 50000, disabled: maxAudience < 50000, active: maxAudience == 50000 }"
                    class="col bs-wizard-step"
                    @click="changeMaxAudience(50000)"
                  >
                    <div class="text-center bs-wizard-stepnum">50.000</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>
                  <div :class="maxAudience >= 50001 ? 'active' : 'disabled'" class="col-2 bs-wizard-step" @click="changeMaxAudience(50001)">
                    <div class="text-center bs-wizard-stepnum">+50.000</div>
                    <div class="progress"><div class="progress-bar"></div></div>
                    <span class="bs-wizard-dot"></span>
                  </div>
                </div>
              </div>
              <PayPeriodToggle v-if="currentStep == 1" :text="$t('managePlan.annual-payment')" />
            </CCol>
          </CRow>
        </CCol>

        <CCol sm="12" class="mt-3 pl-5 pr-5" v-if="currentStep == 1">
          <CardPlanDetails
            v-if="maxAudience < 50001 && !isFormActive"
            :plans="managePlans.plans"
            :plansCatalog="managePlans.plansCatalog"
            :currentPlan="managePlans.organizationPlan.plan"
            :isOrganizationApproved="isOrganizationApproved"
          />
          <NeedSomething v-if="maxAudience < 50001 && !isFormActive" @clicked="isFormActive = !isFormActive" />
          <PlansFeaturesTable
            v-if="maxAudience < 50001 && !isFormActive"
            :plans="managePlans.plans"
            :plansCatalog="managePlans.plansCatalog"
            :currentPlan="managePlans.organizationPlan.plan"
            :isOrganizationApproved="isOrganizationApproved"
          />
          <ContactForm v-if="maxAudience > 50000 || isFormActive" @sentSuccessfully="handleContactFormSent" />
          <AskedQuestions />
        </CCol>

        <CCol xs="12" sm="12" class="mt-4" v-if="currentStep == 2">
          <CContainer class="py-4">
            <CRow class="pageManageBalance justify-content-md-center">
              <CCol xs="12" sm="6">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t('manageplan.component.card.billing-information.title') }}
                    </span>
                  </template>
                  <FormBilling />
                </WrapperCard>
              </CCol>
              <CCol xs="12" sm="5">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t(`manageplan.component.card.plan.title`) }}
                    </span>
                    <a href="" class="font-weight-normal" @click.prevent="goToStep(1)">
                      {{ $t(`manageplan.component.card.plan.edit`) }}
                    </a>
                  </template>
                  <CardPlan />
                  <!-- <CardPlan place="summary" /> -->
                  <!-- <PayPeriod /> -->
                  <CardSubTotal />
                  <CardTotals />
                </WrapperCard>
              </CCol>
            </CRow>
          </CContainer>
        </CCol>

        <CCol xs="12" sm="12" class="mt-4" v-if="currentStep == 3">
          <CContainer class="py-4">
            <CRow class="pageManageBalance justify-content-md-center">
              <CCol xs="12" sm="6">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t('manageplan.component.card.payment-method.title') }}
                    </span>
                  </template>
                  <CardPaymentMethod />
                </WrapperCard>
              </CCol>
              <CCol xs="12" sm="5">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t(`manageplan.component.card.plan.title`) }}
                    </span>
                    <a href="" class="font-weight-normal" @click.prevent="goToStep(1)">
                      {{ $t(`manageplan.component.card.plan.edit`) }}
                    </a>
                  </template>
                  <CardPlan />
                  <!-- <PayPeriod /> -->
                  <CardSubTotal />
                  <CardTotals />
                </WrapperCard>
              </CCol>
            </CRow>
          </CContainer>
        </CCol>

        <CCol sm="12" class="mt-4" v-if="currentStep == 4">
          <CContainer class="py-4">
            <CRow class="pageManageBalance justify-content-md-center">
              <CCol xs="12" sm="6">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t(`manageplan.component.card.plan-detail.title`) }}
                    </span>
                    <a href="" class="font-weight-normal" @click.prevent="goToStep(1)">
                      {{ $t(`manageplan.component.card.plan.edit`) }}
                    </a>
                  </template>
                  <CardPlanSummary />
                </WrapperCard>
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t('manageplan.component.card.billing-information.title') }}
                    </span>
                    <a href="#" class="font-weight-normal" @click.prevent="goToStep(2)">
                      {{ $t('manageplan.component.card.billing-information.edit') }}
                    </a>
                  </template>
                  <CardBillingInfo />
                </WrapperCard>
              </CCol>
              <CCol xs="12" sm="5">
                <WrapperCard>
                  <template slot="title">
                    <span class="font-xl">
                      {{ $t('manageplan.component.card.payment-method.title') }}
                    </span>
                    <a href="" class="font-weight-normal" @click.prevent="goToStep(3)">
                      {{ $t(`manageplan.component.card.plan.edit`) }}
                    </a>
                  </template>
                  <CardPayment />
                  <CardVoucher @updateTotal="updateTotal()" />
                  <!-- <CardCodePromo /> -->
                  <CardTotals :key="totalPrice" v-if="isUpdateTotal" :hasPurchases="totalPrice >= 0 && currentStep == 4" />
                </WrapperCard>
              </CCol>
            </CRow>
          </CContainer>
        </CCol>
      </template>
    </CRow>

    <CModal
      :title="$t(`manageplan.component.card.plan.${planName}`)"
      :show.sync="modalPlanDetails"
      @update:show="closeModal()"
      @keyup.esc="closeModal()"
      :no-close-on-backdrop="true"
      :centered="true"
    >
      <template #header></template>
      <ModalPlanDetail />

      <template #footer>
        <div v-if="true"></div>
      </template>
    </CModal>

    <CModal :show.sync="modalpayment" :no-close-on-backdrop="true" :centered="true">
      <template #header>
        <div v-if="true"></div>
      </template>
      <component v-if="modalpayment" :is="modalComponent"></component>
      <template #footer>
        <div v-if="true"></div>
      </template>
    </CModal>
  </CContainer>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import PayPeriodToggle from '../components/managePlans/PayPeriodToggle.vue'
import Steps from '../components/managePlans/Steps.vue'
import AskedQuestions from '../components/managePlans/AskedQuestions.vue'
import ContactForm from '../components/managePlans/ContactForm.vue'
import NeedSomething from '../components/managePlans/NeedSomething.vue'
import CardPlanDetails from '../components/managePlans/CardPlanDetails.vue'
import PlansFeaturesTable from '../components/managePlans/PlansFeaturesTable.vue'
import WrapperCard from '../components/cards/WrapperCard.vue'
import CardPlan from '../components/cards/CardPlan.vue'
import CardPlanSummary from '../components/cards/CardPlanSummary.vue'
import CardTotals from '../components/cards/CardTotals.vue'
import PayPeriod from '../components/cards/PayPeriod.vue'
import CardSubTotal from '../components/cards/CardSubTotal.vue'
import CardPayment from '../components/cards/CardPayment.vue'
import FormBilling from '../components/FormBilling.vue'
import CardBillingInfo from '../components/cards/CardBillingInfo.vue'
import CardPaymentMethod from '../components/cards/CardPaymentMethod.vue'
import CardCodePromo from '../components/cards/CardCodePromo.vue'
import CardVoucher from '../components/cards/CardVoucher.vue'

import { getItem } from '@/utils/StorageManager'
import services from '@/services.js'
import { mapState } from 'vuex'

// const typeList = {
//   email: 1,
//   sms: 2,
// }

export default {
  name: 'ManagePlans',
  components: {
    PayPeriodToggle,
    Steps,
    NeedSomething,
    AskedQuestions,
    ContactForm,
    CardPlanDetails,
    PlansFeaturesTable,
    PageHeader,
    WrapperCard,
    CardPlan,
    CardPlanSummary,
    CardTotals,
    PayPeriod,
    CardSubTotal,
    FormBilling,
    CardPaymentMethod,
    CardPayment,
    CardBillingInfo,
    CardCodePromo,
    CardVoucher,
    ModalLoadingPayment: () => import('./../components/modals/ModalLoadingPayment.vue'),
    ModalSuccessPayment: () => import('./../components/modals/ModalSuccessPayment.vue'),
    ModalContactForm: () => import('./../components/modals/ModalContactForm.vue'),
    ModalErrorPayment: () => import('./../components/modals/ModalErrorPayment.vue'),
    ModalPlanDetail: () => import('./../components/modals/ModalPlanDetail.vue'),
  },

  data() {
    return {
      isUpdateTotal: true,
      loading: false,
      organizationId: null,
      targetOrganizationId: false,
      organizationName: '',
      currentBalance: [],
      channel: null,
      edit: true,
      insertBillingInfo: false,
      optionsCountriesNames: [],
      socialDesigination: '',
      taxIdentificationNumber: '',
      address: '',
      postalCode: '',
      city: '',
      country: '',
      phoneCountryIndicator: '',
      phoneNumber: '',
      email: '',
      managePlans: [],
      selectedPlanId: '',
      selectedPlanName: '',
      maxAudience: 500,
      maxEmailsIncluded: 2000,
      billingInfo: null,
      isFormActive: false,
      balanceOrganization: undefined,
    }
  },

  created() {
    this.setStateSteps()
    this.organizationId = this.$route.params.organizationId
    this.targetOrganizationId = this.$route.params.targetOrganizationId
    this.organizationName = this.$store.state.auth.organizationName
  },

  beforeDestroy() {
    this.$store.commit('backOffice/steps/RESET_STEP')
    this.$store.commit('backOffice/steps/RESET_PAYMENT')
  },

  mounted() {
    this.billingInfo = this.$store.state.backOffice.steps.billingInfo
    this.setStateSteps()
  },

  beforeMount() {
    this.getPlans()
    this.getBalance()
  },

  computed: {
    ...mapState('backOffice/steps', ['totalPrice']),
    isOrganizationPlanSuspended() {
      return this.$route.query.suspendedPlan
    },
    currentStep() {
      if (this.$store.state.backOffice.steps.currentStep == 1) {
        // this.$store.dispatch('backOffice/steps/initCommit', 'RESET_STEP')
      }
      return this.$store.state.backOffice.steps.currentStep || 1
    },

    planName() {
      return this.$store.state.backOffice.steps.selectedPlanName.toLowerCase()
    },

    modalPlanDetails() {
      return this.$store.state.backOffice.steps.modalPlanDetails
    },

    modalpayment() {
      return this.$store.state.backOffice.steps.modalpayment
    },

    modalComponent() {
      return this.$store.state.backOffice.steps.componentOpenedInModal
    },

    isOrganizationApproved() {
      return this.balanceOrganization?.isApproved
    },

    // totalPriceWithIva() {
    //   return this.$store.state.backOffice.steps.totalPrice
    // },
    // totalPrice() {
    //   return this.$store.state.backOffice.steps.totalPrice
    // },
  },

  methods: {
    handleContactFormSent() {
      this.isFormActive = false
    },
    updateTotal() {
      this.isUpdateTotal = false
      this.$nextTick(() => {
        this.isUpdateTotal = true
      })
    },

    getPlans() {
      this.loading = true
      services
        .getPlans(this.organizationId)
        .then((res) => {
          this.managePlans = res.data.data
        })
        .finally(() => {
          const currentPlanTier = this.managePlans?.organizationPlan?.plan?.planTiers
          const currentMaxAudience = currentPlanTier?.maxAudienceRecipients
          const currentPaymentPeriod = currentPlanTier?.periodPlanTierPrice.paymentPeriod

          this.changeMaxAudience(currentMaxAudience || 500)
          this.$store.dispatch('backOffice/steps/updateInfoPropStep', { prop: 'paymentPeriod', data: currentPaymentPeriod || 1 })
          this.loading = false
        })
    },

    getBalance() {
      services.getCurrentBalanceOrganization(this.organizationId).then((res) => {
        this.balanceOrganization = res.data.data
      })
    },

    goToStep(step, selectedPlan = null) {
      if (step == 3 && this.billingInfo) {
        this.$store
          .dispatch('backOffice/steps/validateBillingInfo', {
            organizationId: this.organizationId,
            prop: 'billingInfo',
            info: this.billingInfo,
          })
          .then(() => {
            // ao entrar na pagina escolha de método de pagamento deve-se limpar os dados da state referentes a criação do paymentIntent
            this.$store.dispatch('backOffice/steps/initCommit', 'RESET_PAYMENT')
            this.$store.dispatch('backOffice/steps/setCurrentStep', 3)
            this.$store.dispatch('backOffice/steps/updateInfoPropStep', { prop: 'maxStep', data: 3 })
          })
          .catch((error) => {
            console.log('catch', error, error.response)
            this.messages.push({
              status: 'danger',
              text: error.response.data.data.message || 'erro',
            })
          })
      }

      if (selectedPlan !== null) {
        this.selectedPlanId = selectedPlan.id
        this.selectedPlanName = selectedPlan.name
      }

      this.$store.dispatch('backOffice/steps/setCurrentStep', step)
      this.$store.dispatch('backOffice/steps/updateInfoPropStep', { prop: 'maxStep', data: step })
    },

    changeMaxAudience(value) {
      if (value < 50001) {
        this.isFormActive = false
      }

      this.maxAudience = value
      this.maxEmailsIncluded = value * 4
      let maxEmailsIncluded = value * 4
      this.$store.dispatch('backOffice/steps/updateInfoPropStep', { prop: 'maxAudience', data: value })
      this.$store.dispatch('backOffice/steps/updateInfoPropStep', { prop: 'maxEmailsIncluded', data: maxEmailsIncluded })
    },

    getPriceByPlanIdAndMaxAudience(planId) {
      let plans = this.managePlans.plans //plansCatalog
      let plan = plans.find((item) => item.id === planId)
      let planTier = plan.planTiers.find((it) => it.maxAudienceRecipients == this.maxAudience)
      if (typeof planTier != 'undefined') {
        return planTier.periodPlanTierPrice.price
      }
      return
    },

    getMaxEmailByPlanIdAndMaxAudience(planId) {
      let plans = this.managePlans.plans //plansCatalog
      let plan = plans.find((item) => item.id === planId)
      let planTier = plan.planTiers.find((it) => it.maxAudienceRecipients == this.maxAudience)
      if (typeof planTier != 'undefined') {
        this.maxEmailsIncluded = planTier.maxEmailsPerMonth
        return planTier.maxEmailsPerMonth
      }
      return
    },

    getFeaturesByPlan(featureId, planId) {
      let plan = this.managePlans.plans.find((it) => it.id === planId) //plansCatalog
      let plansFeatures = []

      if (plan.features.length > 0) {
        let ft = plan.features.find((item) => item.fetaureType === featureId)
        if (typeof ft != 'undefined') {
          plansFeatures.push(ft)
        } else {
          plansFeatures.push({ id: 'null', metadata: { valueKind: 'null' } })
        }
      }
      return plansFeatures
    },

    setModal(data) {
      this.$store.dispatch('backOffice/steps/setModalComponent', data)
    },

    closeModal() {
      this.$store.dispatch('backOffice/steps/closeModalComponent')
    },

    currentStepStep(stepNumber) {
      this.$store.dispatch('backOffice/steps/setCurrentStep', stepNumber)
    },

    setStateSteps() {
      const stateStep = JSON.parse(getItem('stateStep'))

      if (stateStep) {
        this.$store.dispatch('backOffice/steps/updateInfoStep', stateStep)
        this.currentStepStep(stateStep.currentStep)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pageManageBalance__campaignName {
  border-bottom: 1px solid var(--gray-300);
  line-height: 1.5em;
}

.bold {
  font-weight: bold;
}

ul.plan-steps {
  max-width: 60%;
  height: 30px;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  background-color: transparent !important;
}

ul.progress-bar {
  max-width: 80%;
  height: 30px;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  background-color: transparent !important;
}
ul.progress-bar::after {
  content: '';
  position: absolute;
  left: 5px;
  background: #777;
  width: 100vw;
  height: 5px;
  margin-top: 6px;
}
ul.progress-bar li {
  background: #000;
  border-radius: 100px;
  width: 18px;
  height: 18px;
  z-index: 1;
  position: relative;
}
ul.progress-bar li.stop ~ li {
  background: #777;
}
ul.progress-bar li.stop ~ li::after {
  height: 0;
}
ul.progress-bar li::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 5px;
  background: #000;
  width: 5px;
  height: 100vh;
}

.bs-wizard {
  margin-top: 40px;
}

/*Form Wizard*/
.bs-wizard {
  border-bottom: solid 1px #e0e0e0;
  padding: 0 0 10px 0;
  margin-top: 5px;
}
.bs-wizard > .bs-wizard-step {
  padding: 0;
  position: relative;
}
// .bs-wizard > .bs-wizard-step + .bs-wizard-step {
// }
.bs-wizard > .bs-wizard-step .bs-wizard-stepnum {
  color: #868e96;
  font-size: 14px;
  margin-bottom: 5px;
}
.bs-wizard > .bs-wizard-step .bs-wizard-info {
  color: rgb(102, 102, 102);
  font-size: 14px;
}
.bs-wizard > .bs-wizard-step > .bs-wizard-dot {
  position: absolute;
  width: 26px; // 30 30
  height: 26px;
  display: block;
  background: #7fcfe6;
  top: 45px;
  left: 50%;
  margin-top: -15px;
  margin-left: -15px;
  border-radius: 50%;
}
.bs-wizard > .bs-wizard-step > .bs-wizard-dot:after {
  content: ' ';
  width: 11px; //14 14
  height: 11px;
  background: #ffe8e1;
  border-radius: 50px;
  position: absolute;
  top: 7px; //8 8
  left: 7.5px;
}
.bs-wizard > .bs-wizard-step > .progress {
  position: relative;
  border-radius: 0px;
  height: 8px;
  box-shadow: none;
  margin: 20px 0;
}
.bs-wizard > .bs-wizard-step > .progress > .progress-bar {
  width: 0px;
  box-shadow: none;
  background: #7fcfe6;
}
.bs-wizard > .bs-wizard-step.complete > .progress > .progress-bar {
  width: 100%;
}
.bs-wizard > .bs-wizard-step.active > .progress > .progress-bar {
  width: 50%;
}
.bs-wizard > .bs-wizard-step:first-child.active > .progress > .progress-bar {
  width: 0%;
}
.bs-wizard > .bs-wizard-step:last-child.active > .progress > .progress-bar {
  width: 100%;
}
.bs-wizard > .bs-wizard-step.disabled > .bs-wizard-dot {
  background-color: #868585;
}
.bs-wizard > .bs-wizard-step.disabled > .bs-wizard-dot:after {
  opacity: 0;
}
.bs-wizard > .bs-wizard-step:first-child > .progress {
  left: 50%;
  width: 50%;
}
.bs-wizard > .bs-wizard-step:last-child > .progress {
  width: 50%;
}
.progress {
  background-color: #afafaf;
}
.bs-wizard-dot {
  cursor: pointer;
}
/*END Form Wizard*/

.btn-default {
  background-color: rgb(189, 189, 189);
  color: rgb(92, 92, 92);
}

.linhaBusiness {
  color: #7fcfe6;
  position: absolute;
  right: 31%;
  bottom: 5px;
}

.titleBusiness {
  position: relative;
  height: 60px;
}

.subTitleBusiness {
  color: #868e96;
  font-size: 18px;
}

.container {
  padding-right: 0px;
  padding-left: 0px;
}

.stepOneFirstBlock {
  background-color: #fff;
}
</style>
